import React from 'react';
import { useUserData } from '../context/UserDataContext';
import { generateAlerts } from '../lib/generateAlerts';
import { AlertTriangle, CheckCircle, Info, TrendingUp, TrendingDown, ArrowRight, Bell, Sparkles, Link2 } from 'lucide-react';
import FinancialHealthScorecard from './FinancialHealthScorecard';
import RetirementOutlook from './RetirementOutlook';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const pct = (a, b) => Math.round((a / b) * 100);
const TODAY = new Date().toISOString().slice(0, 10);

function KPICard({ label, value, sub, trend, positive }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <div className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-2">{label}</div>
      <div className="text-2xl font-bold text-slate-100 mb-1">{value}</div>
      {sub && (
        <div className={`text-xs flex items-center gap-1 ${positive ? 'text-emerald-400' : 'text-rose-400'}`}>
          {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {sub}
        </div>
      )}
      {trend && <div className="text-xs text-slate-500 mt-1">{trend}</div>}
    </div>
  );
}

function AlertCard({ alert, setActiveTab }) {
  const styles = {
    warning: { bg: 'bg-amber-950/60 border-amber-700/50', icon: <AlertTriangle size={16} className="text-amber-400 flex-shrink-0" /> },
    danger:  { bg: 'bg-rose-950/60 border-rose-700/50',   icon: <AlertTriangle size={16} className="text-rose-400 flex-shrink-0" /> },
    info:    { bg: 'bg-blue-950/60 border-blue-700/50',   icon: <Info size={16} className="text-blue-400 flex-shrink-0" /> },
    success: { bg: 'bg-emerald-950/60 border-emerald-700/50', icon: <CheckCircle size={16} className="text-emerald-400 flex-shrink-0" /> },
  };
  const s = styles[alert.type] || styles.info;
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${s.bg}`}>
      {s.icon}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-200">{alert.title}</div>
        <div className="text-xs text-slate-400 mt-0.5 leading-snug">{alert.desc}</div>
      </div>
      {alert.action && (
        <button
          onClick={() => setActiveTab(alert.tab)}
          className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-0.5 flex-shrink-0 whitespace-nowrap"
        >
          {alert.action} <ArrowRight size={11} />
        </button>
      )}
    </div>
  );
}

export default function Overview({ setActiveTab }) {
  const {
    netWorthData, budgetData, goalsData, retirementData, rsusData,
    calendarData, userProfile, isNewUser, dataLoading, linkedAccountsData,
  } = useUserData();

  const totalAssets      = (netWorthData.assets      || []).reduce((s, a) => s + a.value, 0);
  const totalLiabilities = (netWorthData.liabilities  || []).reduce((s, l) => s + l.value, 0);
  const netWorth         = totalAssets - totalLiabilities;
  const successRate      = retirementData?.successRate || 0;

  // Use real transaction data for monthly spend when available
  const allTxns   = budgetData.transactions || [];
  const now        = new Date();
  const mStart     = new Date(now.getFullYear(), now.getMonth(), 1);
  const mEnd       = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  const totalSpent = allTxns.length > 0
    ? allTxns
        .filter(t => { const d = new Date(t.date + 'T12:00:00'); return t.kind === 'expense' && d >= mStart && d <= mEnd; })
        .reduce((s, t) => s + t.amount, 0)
    : (budgetData.categories || []).reduce((s, c) => s + (c.spent || 0), 0);

  const alerts      = generateAlerts({ budgetData, netWorthData, retirementData, rsusData });
  const displayName = userProfile?.name || '';
  const upcoming    = (calendarData || []).filter(e => e.date >= TODAY).slice(0, 5);

  // Plaid linked accounts
  const plaidAccounts = (linkedAccountsData || []).filter(a => a.connectionType === 'plaid');

  // Recent transactions (last 8, newest first)
  const recentTxns = [...allTxns]
    .filter(t => t.kind === 'expense' || t.kind === 'income')
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 8);

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-96">
        <div className="flex gap-1.5">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">
            Good morning{displayName ? `, ${displayName}` : ''} 👋
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} · Here's your financial picture
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {plaidAccounts.length > 0 && (
            <div className="flex items-center gap-1.5 bg-emerald-950/60 border border-emerald-700/40 rounded-lg px-3 py-2">
              <Link2 size={13} className="text-emerald-400" />
              <span className="text-xs text-emerald-300 font-medium">
                {plaidAccounts.length} account{plaidAccounts.length !== 1 ? 's' : ''} linked
              </span>
              <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
            </div>
          )}
          {alerts.length > 0 && (
            <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2">
              <Bell size={15} className="text-slate-400" />
              <span className="text-sm text-slate-300 font-medium">{alerts.length} alerts</span>
              <div className="w-2 h-2 bg-rose-400 rounded-full animate-pulse" />
            </div>
          )}
        </div>
      </div>

      {/* Welcome banner for new users */}
      {isNewUser && (
        <div className="bg-gradient-to-br from-blue-900/60 via-indigo-900/40 to-purple-900/30 border border-blue-700/40 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-900/40">
              <Sparkles size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-100 mb-1">Welcome to Finovo! Let's get started 🎉</h2>
              <p className="text-slate-300 text-sm mb-4 leading-relaxed">
                Your dashboard is ready. Start by adding your accounts and budget to see your complete financial picture.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setActiveTab('networth')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors shadow-lg shadow-blue-900/30"
                >
                  Add Accounts →
                </button>
                <button
                  onClick={() => setActiveTab('budget')}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-200 text-sm font-medium rounded-lg transition-colors"
                >
                  Set Up Budget →
                </button>
                <button
                  onClick={() => setActiveTab('goals')}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-200 text-sm font-medium rounded-lg transition-colors"
                >
                  Set Goals →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Net Worth"
          value={fmt(netWorth)}
          trend="total assets minus liabilities"
          positive={netWorth >= 0}
        />
        <KPICard
          label="Total Assets"
          value={fmt(totalAssets)}
          trend="across all accounts"
          positive
        />
        <KPICard
          label="Monthly Spent"
          value={fmt(totalSpent)}
          sub={budgetData.income > 0 ? `${pct(totalSpent, budgetData.income)}% of income` : undefined}
          positive={budgetData.income > 0 && totalSpent <= budgetData.income}
          trend={budgetData.income > 0 ? `of ${fmt(budgetData.income)} income` : 'Set income in Budget →'}
        />
        <KPICard
          label="Retirement Success"
          value={successRate > 0 ? `${successRate}%` : '—'}
          sub={successRate > 0 ? (successRate >= 85 ? 'On track ✓' : 'Below target') : undefined}
          positive={successRate >= 85}
          trend={successRate > 0 ? 'Monte Carlo · 500 sims' : 'Run planner to calculate'}
        />
      </div>

      {/* Financial Health Scorecard */}
      <FinancialHealthScorecard setActiveTab={setActiveTab} />

      {/* Retirement Outlook */}
      <RetirementOutlook setActiveTab={setActiveTab} />

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alerts */}
        <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-200 text-base">Smart Alerts</h2>
            {alerts.filter(a => a.type === 'danger').length > 0 && (
              <span className="text-xs bg-rose-500 text-white rounded-full px-2 py-0.5 font-medium">
                {alerts.filter(a => a.type === 'danger').length} critical
              </span>
            )}
          </div>
          {alerts.length > 0 ? (
            <div className="space-y-2">
              {alerts.map(a => <AlertCard key={a.id} alert={a} setActiveTab={setActiveTab} />)}
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 text-center">
              <CheckCircle size={32} className="text-emerald-400 mb-3" />
              <p className="text-slate-300 font-medium mb-1">All clear!</p>
              <p className="text-sm text-slate-500">
                {isNewUser
                  ? 'Add your financial data to get personalized alerts and insights.'
                  : 'No alerts — your finances look great.'}
              </p>
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Monte Carlo teaser */}
          <div className="bg-gradient-to-br from-blue-900/60 to-purple-900/40 border border-blue-700/40 rounded-xl p-5">
            <div className="text-xs text-blue-300 font-medium uppercase tracking-wider mb-1">Retirement Planner</div>
            {successRate > 0 ? (
              <>
                <div className="flex items-end gap-2 mb-1">
                  <span className="text-4xl font-bold text-white">{successRate}%</span>
                  <span className="text-blue-300 text-sm mb-1">success rate</span>
                </div>
                <div className="text-xs text-blue-200/70 mb-3">500 Monte Carlo simulations</div>
                <div className="w-full bg-blue-900/50 rounded-full h-1.5 mb-3">
                  <div className="bg-amber-400 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(successRate, 100)}%` }} />
                </div>
              </>
            ) : (
              <div className="py-2 mb-3">
                <p className="text-blue-200/70 text-sm leading-relaxed">
                  Run the Monte Carlo planner to see your retirement success rate.
                </p>
              </div>
            )}
            <button
              onClick={() => setActiveTab('retirement')}
              className="text-sm text-blue-300 hover:text-blue-200 flex items-center gap-1 font-medium transition-colors"
            >
              Open Planner <ArrowRight size={14} />
            </button>
          </div>

          {/* Goals snapshot */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-200 text-sm">Goals</h3>
              <button
                onClick={() => setActiveTab('goals')}
                className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-0.5 transition-colors"
              >
                All <ArrowRight size={11} />
              </button>
            </div>
            {(goalsData || []).length > 0 ? (
              <div className="space-y-3">
                {(goalsData || []).slice(0, 4).map(g => {
                  const p = Math.min(Math.round((g.current / g.target) * 100), 100);
                  return (
                    <div key={g.id}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-300 flex items-center gap-1.5">
                          <span>{g.icon}</span>{g.name}
                        </span>
                        <span className="text-xs font-medium text-slate-400">{p}%</span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all ${p >= 80 ? 'bg-emerald-400' : p >= 50 ? 'bg-blue-400' : 'bg-amber-400'}`}
                          style={{ width: `${p}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-xs text-slate-500 mb-2">No goals set yet</p>
                <button
                  onClick={() => setActiveTab('goals')}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Add your first goal →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      {recentTxns.length > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-200 text-base">Recent Transactions</h2>
            <button
              onClick={() => setActiveTab('budget')}
              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-0.5 transition-colors"
            >
              View all <ArrowRight size={11} />
            </button>
          </div>
          <div className="space-y-1.5">
            {recentTxns.map((t, i) => {
              const isExpense = t.kind === 'expense';
              const d = new Date(t.date + 'T12:00:00');
              const needsReview = !t.reviewed && isExpense;
              return (
                <div key={t.id || i} className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-700/40 transition-colors">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                    isExpense ? 'bg-rose-900/40 text-rose-400' : 'bg-emerald-900/40 text-emerald-400'
                  }`}>
                    {isExpense ? '↑' : '↓'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-200 truncate font-medium">{t.description}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {t.category ? (
                        <span className="text-xs text-slate-500">{t.category}</span>
                      ) : needsReview ? (
                        <span className="text-xs text-amber-400">Needs review</span>
                      ) : null}
                      {needsReview && !t.category && (
                        <span className="text-xs px-1.5 py-0.5 bg-amber-900/30 border border-amber-700/30 rounded text-amber-400">Plaid</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`text-sm font-semibold tabular-nums ${isExpense ? 'text-rose-400' : 'text-emerald-400'}`}>
                      {isExpense ? '-' : '+'}{fmt(t.amount)}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming calendar */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-200 text-base">Upcoming</h2>
          <button
            onClick={() => setActiveTab('calendar')}
            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-0.5 transition-colors"
          >
            Full calendar <ArrowRight size={11} />
          </button>
        </div>
        {upcoming.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {upcoming.map((ev, i) => {
              const typeStyle = {
                income:  { bg: 'bg-emerald-950/60 border-emerald-700/40', color: 'text-emerald-300', label: 'Income' },
                bill:    { bg: 'bg-slate-700/60 border-slate-600/40',     color: 'text-slate-300',   label: 'Bill' },
                rsu:     { bg: 'bg-purple-950/60 border-purple-700/40',   color: 'text-purple-300',  label: 'RSU Vest' },
                meeting: { bg: 'bg-blue-950/60 border-blue-700/40',       color: 'text-blue-300',    label: 'Meeting' },
              };
              const ts = typeStyle[ev.type] || typeStyle.bill;
              const d = new Date(ev.date + 'T12:00:00');
              return (
                <div key={i} className={`p-3 rounded-lg border ${ts.bg}`}>
                  <div className="text-xs text-slate-500 mb-1">
                    {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className={`text-xs font-medium ${ts.color} mb-1`}>{ts.label}</div>
                  <div className="text-sm font-medium text-slate-200 leading-tight mb-1">{ev.title}</div>
                  {ev.amount !== 0 && (
                    <div className={`text-sm font-bold ${ev.amount > 0 ? 'text-emerald-400' : 'text-slate-300'}`}>
                      {ev.amount > 0 ? '+' : ''}{fmt(ev.amount)}
                    </div>
                  )}
                  {ev.note && <div className="text-xs text-slate-500 mt-1 leading-snug">{ev.note}</div>}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-slate-500 text-sm mb-2">No upcoming events</p>
            <button
              onClick={() => setActiveTab('calendar')}
              className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              Add to calendar →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
